import React, { useState, useEffect } from 'react';
import {
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckoutIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';

interface CartSidebarProps {
  projectId: string;
  projectName: string;
  clientName: string;
  open: boolean;
  onClose: () => void;
  onCheckout?: () => void;
}

const CartSidebar: React.FC<CartSidebarProps> = ({
  projectId,
  projectName,
  clientName,
  open,
  onClose,
  onCheckout,
}) => {
  const {
    cart,
    loading,
    error,
    fetchCart,
    removeFromCart,
    cartCount,
    cartTotal,
  } = useCart(projectId);

  const [removingItemId, setRemovingItemId] = useState<string | null>(null);

  useEffect(() => {
    if (open && projectId) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]); // Only depend on open and projectId, not fetchCart to avoid infinite loop

  const handleRemoveItem = async (orderId: string) => {
    setRemovingItemId(orderId);
    try {
      await removeFromCart(orderId);
    } catch (err) {
      console.error('Failed to remove item:', err);
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    }
  };

  const handleContinueShopping = () => {
    onClose();
  };

  const budgetUsedPercent = cart?.budgetUsedPercent || 0;
  const budgetRemaining = cart?.budgetRemaining || 0;
  const projectBudget = cart?.projectBudget || 0;
  const monthlySpent = cart?.monthlySpent;
  const monthPlacementSpent = cart?.monthPlacementSpent || 0;
  const monthOrderSpent = cart?.monthOrderSpent || 0;

  const getBudgetColor = () => {
    if (budgetUsedPercent >= 100) return 'error';
    if (budgetUsedPercent >= 80) return 'warning';
    return 'success';
  };

  return (
    <>
      <style>{`
        .cart-drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1300;
          display: ${open ? 'block' : 'none'};
        }
        .cart-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 450px;
          max-width: 100%;
          background: #FFFFFF;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          z-index: 1301;
          transform: translateX(${open ? '0' : '100%'});
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .cart-drawer-header {
          background: #2563EB;
          color: white;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cart-drawer-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 600;
        }
        .cart-drawer-close {
          padding: 8px;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
          color: white;
        }
        .cart-drawer-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        .cart-drawer-close svg {
          width: 20px;
          height: 20px;
        }
        .cart-project-info {
          padding: 12px 20px;
          background: #F8FAFC;
          border-bottom: 1px solid #E2E8F0;
        }
        .cart-project-label {
          font-size: 12px;
          color: #64748B;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }
        .cart-project-value {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
        }
        .cart-budget-info {
          padding: 12px 20px;
          background: #FFFFFF;
          border-bottom: 1px solid #E2E8F0;
        }
        .cart-budget-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .cart-budget-label {
          font-size: 13px;
          color: #64748B;
        }
        .cart-budget-value {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
        }
        .cart-budget-value.negative {
          color: #DC2626;
        }
        .cart-budget-value.positive {
          color: #059669;
        }
        .cart-progress-container {
          margin-top: 12px;
        }
        .cart-progress-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .cart-progress-label {
          font-size: 11px;
          color: #64748B;
        }
        .cart-progress-value {
          font-size: 11px;
          font-weight: 600;
          color: #1E293B;
        }
        .cart-progress-bar {
          height: 8px;
          background: #E2E8F0;
          border-radius: 4px;
          overflow: hidden;
        }
        .cart-progress-fill {
          height: 100%;
          background: #2563EB;
          transition: width 0.3s ease;
        }
        .cart-progress-fill.warning {
          background: #F59E0B;
        }
        .cart-progress-fill.error {
          background: #EF4444;
        }
        .cart-warning {
          margin-top: 12px;
          padding: 8px 12px;
          background: #FEF3C7;
          border: 1px solid #F59E0B;
          border-radius: 6px;
          font-size: 12px;
          color: #92400E;
        }
        .cart-items-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
        }
        .cart-loading {
          display: flex;
          justify-content: center;
          padding: 40px;
        }
        .cart-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #E2E8F0;
          border-top-color: #2563EB;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .cart-error {
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #EF4444;
          border-radius: 6px;
          font-size: 13px;
          color: #991B1B;
          margin-bottom: 16px;
        }
        .cart-empty {
          text-align: center;
          padding: 60px 20px;
        }
        .cart-empty svg {
          width: 64px;
          height: 64px;
          color: #CBD5E1;
          margin-bottom: 16px;
        }
        .cart-empty-title {
          font-size: 16px;
          font-weight: 600;
          color: #64748B;
          margin-bottom: 8px;
        }
        .cart-empty-text {
          font-size: 13px;
          color: #94A3B8;
        }
        .cart-item {
          padding: 12px 0;
          border-bottom: 1px solid #E2E8F0;
        }
        .cart-item:last-child {
          border-bottom: none;
        }
        .cart-item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
        }
        .cart-item-url {
          font-size: 13px;
          font-weight: 600;
          color: #1E293B;
          flex: 1;
          margin-right: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cart-item-delete {
          padding: 6px;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }
        .cart-item-delete:hover {
          background: #FEF2F2;
        }
        .cart-item-delete svg {
          width: 16px;
          height: 16px;
          color: #EF4444;
        }
        .cart-item-chips {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-bottom: 6px;
        }
        .cart-item-chip {
          padding: 3px 6px;
          background: #F1F5F9;
          color: #475569;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 500;
        }
        .cart-item-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cart-item-publisher {
          font-size: 11px;
          color: #64748B;
        }
        .cart-item-price {
          font-size: 14px;
          font-weight: 600;
          color: #2563EB;
        }
        .cart-drawer-footer {
          padding: 12px 20px;
          background: #FFFFFF;
          border-top: 1px solid #E2E8F0;
        }
        .cart-total-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .cart-total-label {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
        }
        .cart-total-value {
          font-size: 15px;
          font-weight: 600;
          color: #2563EB;
        }
        .cart-actions {
          display: flex;
          gap: 8px;
        }
        .cart-btn {
          flex: 1;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          border: none;
          font-family: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .cart-btn svg {
          width: 16px;
          height: 16px;
        }
        .cart-btn-primary {
          background: #2563EB;
          color: white;
        }
        .cart-btn-primary:hover {
          background: white;
          color: #1E293B;
          border: 1px solid #E2E8F0;
        }
        .cart-btn-secondary {
          background: white;
          color: #475569;
          border: 1px solid #E2E8F0;
        }
        .cart-btn-secondary:hover {
          background: #F8FAFC;
        }
      `}</style>
      <div className="cart-drawer-overlay" onClick={onClose} />
      <div className="cart-drawer">
        {/* Header */}
        <div className="cart-drawer-header">
          <div className="cart-drawer-title">
            <CartIcon />
            <span>Shopping Cart ({cartCount})</span>
          </div>
          <button className="cart-drawer-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Project Info */}
        <div className="cart-project-info">
          <div className="cart-project-label">Client</div>
          <div className="cart-project-value">{clientName}</div>
          <div className="cart-project-label" style={{ marginTop: '12px' }}>Project</div>
          <div className="cart-project-value">{projectName}</div>
        </div>

        {/* Budget Info */}
        {cart && (
          <div className="cart-budget-info">
            <div className="cart-budget-row">
              <span className="cart-budget-label">Monthly Budget</span>
              <span className="cart-budget-value">${projectBudget.toFixed(2)}/mo</span>
            </div>
            {monthlySpent !== undefined && (
              <>
                <div className="cart-budget-row">
                  <span className="cart-budget-label">Placements this month</span>
                  <span className="cart-budget-value">${monthPlacementSpent.toFixed(2)}</span>
                </div>
                <div className="cart-budget-row">
                  <span className="cart-budget-label">Orders this month</span>
                  <span className="cart-budget-value">${monthOrderSpent.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="cart-budget-row">
              <span className="cart-budget-label">Cart Total</span>
              <span className="cart-budget-value">${cartTotal.toFixed(2)}</span>
            </div>
            <div className="cart-budget-row">
              <span className="cart-budget-label">
                {budgetRemaining < 0 ? 'Over Budget' : 'Remaining this month'}
              </span>
              <span className={`cart-budget-value ${budgetRemaining < 0 ? 'negative' : 'positive'}`}>
                {budgetRemaining < 0 ? '-' : ''}${Math.abs(budgetRemaining).toFixed(2)}
              </span>
            </div>
            <div className="cart-progress-container">
              <div className="cart-progress-header">
                <span className="cart-progress-label">Budget Used</span>
                <span className="cart-progress-value">{budgetUsedPercent.toFixed(1)}%</span>
              </div>
              <div className="cart-progress-bar">
                <div 
                  className={`cart-progress-fill ${
                    budgetUsedPercent >= 100 ? 'error' : 
                    budgetUsedPercent >= 80 ? 'warning' : ''
                  }`}
                  style={{ width: `${Math.min(budgetUsedPercent, 100)}%` }}
                />
              </div>
            </div>
            {budgetUsedPercent >= 100 && (
              <div className="cart-warning">
                Budget exceeded! Please review cart items.
              </div>
            )}
          </div>
        )}

        {/* Cart Items */}
        <div className="cart-items-container">
          {loading && !cart && (
            <div className="cart-loading">
              <div className="cart-spinner" />
            </div>
          )}

          {error && (
            <div className="cart-error">
              {error}
            </div>
          )}

          {!loading && cart && cartCount === 0 && (
            <div className="cart-empty">
              <CartIcon />
              <div className="cart-empty-title">Your cart is empty</div>
              <div className="cart-empty-text">Browse sites and add them to your cart</div>
            </div>
          )}

          {cart && cartCount > 0 && (
            <div>
              {cart.cartItems.map((item, index) => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-header">
                    <div className="cart-item-url">{item.siteUrl}</div>
                    <button
                      className="cart-item-delete"
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={removingItemId === item.id}
                    >
                      {removingItemId === item.id ? (
                        <div className="cart-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </button>
                  </div>
                  <div className="cart-item-chips">
                    <span className="cart-item-chip">DA: {item.siteDa}</span>
                    <span className="cart-item-chip">DR: {item.siteDr}</span>
                    <span className="cart-item-chip">Traffic: {item.siteTraffic.toLocaleString()}</span>
                  </div>
                  <div className="cart-item-footer">
                    <span className="cart-item-publisher">{item.publisherName}</span>
                    <span className="cart-item-price">${item.price.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart && cartCount > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-total-row">
              <span className="cart-total-label">Total</span>
              <span className="cart-total-value">${cartTotal.toFixed(2)}</span>
            </div>
            <div className="cart-actions">
              <button className="cart-btn cart-btn-secondary" onClick={handleContinueShopping}>
                <BackIcon />
                Continue Shopping
              </button>
              <button className="cart-btn cart-btn-primary" onClick={handleCheckout} disabled={loading}>
                <CheckoutIcon />
                Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
